import React, { useCallback, useEffect, useState } from "react";
import { Car, Loader2, Info, CalendarClock, MapPin, Check, X, Flag, Star, Bell, MessageSquare, Send, TrendingUp, Download, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, Section, StatusBadge, EmptyState } from "../../components/app/ui";
import { RatingBreakdown } from "../../components/app/RatingBreakdown";
import { privateCarApi, blockApi, inr, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { categoryOf, isPrivateCarCategory } from "../../lib/driver";

const REMINDER_CHOICES = [48, 24, 12, 6, 2, 1];

export default function PrivateCarJobs() {
  const { user } = useAuth();
  const isPc = isPrivateCarCategory(categoryOf(user));
  const [pool, setPool] = useState([]);
  const [mine, setMine] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState({ rating: null, rating_count: 0 });
  const [prefs, setPrefs] = useState(user?.pc_reminder_hours || [24, 2]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => { if (Array.isArray(user?.pc_reminder_hours)) setPrefs(user.pc_reminder_hours); }, [user?.pc_reminder_hours]);

  const load = useCallback(() => {
    if (!isPc) return;
    privateCarApi.pool().then(setPool).catch(() => {});
    privateCarApi.driverBookings().then(setMine).catch(() => {});
    privateCarApi.myReviews().then((r) => { setReviews(r.reviews || []); setMyRating({ rating: r.rating, rating_count: r.rating_count }); }).catch(() => {});
    privateCarApi.driverStats().then(setStats).catch(() => {});
  }, [isPc]);
  useEffect(() => { load(); }, [load]);

  const togglePref = async (h) => {
    const next = prefs.includes(h) ? prefs.filter((x) => x !== h) : [...prefs, h];
    setPrefs(next);
    try { const r = await privateCarApi.reminderPrefs(next); setPrefs(r.pc_reminder_hours || next); }
    catch (e) { toast.error(errorMessage(e, "Could not save reminder settings")); }
  };

  const sendReply = async (reviewId) => {
    if (!replyText.trim()) return;
    try {
      await privateCarApi.replyReview(reviewId, replyText.trim());
      toast.success("Reply posted");
      setReplyFor(null); setReplyText("");
      privateCarApi.myReviews().then((r) => setReviews(r.reviews || [])).catch(() => {});
    } catch (e) { toast.error(errorMessage(e, "Could not post reply")); }
  };

  const blockReviewer = async (reviewId) => {
    try {
      await blockApi.block({ review_id: reviewId });
      toast.success("Reviewer blocked. Their reviews are now hidden.");
      privateCarApi.myReviews().then((r) => setReviews(r.reviews || [])).catch(() => {});
    } catch (e) { toast.error(errorMessage(e, "Could not block reviewer")); }
  };

  const run = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(id); toast.success(msg); load(); }
    catch (e) { toast.error(errorMessage(e, "Something went wrong")); }
    finally { setBusyId(null); }
  };

  if (!isPc) {
    return (
      <>
        <PageHeader title="Private car jobs" />
        <EmptyState icon={Car} title="Private-car drivers only" text="Switch your vehicle to a Private Car (4 or 6 seater) in your profile to receive these bookings." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Private car jobs" description="You're listed as a driver for hire (with your car). Accept advance bookings; you keep the full daily fare — the customer pays you directly for the fare, your car's fuel and toll. The customer pays KohimaTaxi a flat ₹300 + 5% GST (₹315) platform fee upfront." />
      <div className="mx-auto max-w-2xl space-y-6">
        <Section title="Booking requests" description={`${pool.length} waiting`} actions={<Button variant="outline" size="sm" onClick={load} data-testid="pc-refresh">Refresh</Button>}>
          {pool.length === 0 ? (
            <EmptyState icon={Info} title="No requests right now" text="Customers who select your car and send a request will appear here." />
          ) : (
            <div className="space-y-2" data-testid="pc-pool">
              {pool.map((b) => (
                <div key={b.booking_id} className="rounded-2xl border border-surface-300 bg-white p-3" data-testid={`pc-pool-${b.booking_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><CalendarClock className="h-4 w-4 text-brand" /> {new Date(b.start_date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {b.days} day{b.days > 1 ? "s" : ""}</span>
                    <span className="rounded-full bg-graphite px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{b.seater}-seater</span>
                  </div>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-soft"><MapPin className="h-3 w-3" /> {b.pickup_address}</p>
                  {b.notes ? <p className="mt-1 text-xs text-ink-muted">“{b.notes}”</p> : null}
                  <p className="mt-1 text-xs text-ink-muted">The customer pays you directly for the fare, fuel &amp; toll. Accept to confirm at your daily rate × {b.days} day{b.days > 1 ? "s" : ""}.</p>
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => run(b.booking_id, privateCarApi.accept, "Booking accepted — it's yours!")} disabled={busyId === b.booking_id} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`pc-accept-${b.booking_id}`}>
                      {busyId === b.booking_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />} Accept
                    </Button>
                    <Button onClick={() => run(b.booking_id, privateCarApi.reject, "Request declined")} disabled={busyId === b.booking_id} variant="outline" className="rounded-full" data-testid={`pc-reject-${b.booking_id}`}>
                      <X className="mr-1.5 h-4 w-4" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {stats && (stats.overall.trips > 0 || (stats.months && stats.months.length > 0)) && (
          <Section title="Monthly summary" description="Your completed private-car trips, earnings (your full daily fare) and ratings." actions={<a href={privateCarApi.earningsExportUrl()} download className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600" data-testid="pc-earnings-export"><Download className="h-3.5 w-3.5" /> Statement (CSV)</a>}>
            <div className="mb-3 grid grid-cols-3 gap-2" data-testid="pc-stats-overall">
              <div className="rounded-xl bg-surface-50 p-2.5 text-center"><p className="text-lg font-bold text-ink">{stats.overall.trips}</p><p className="text-[11px] text-ink-soft">Total trips</p></div>
              <div className="rounded-xl bg-surface-50 p-2.5 text-center"><p className="text-lg font-bold text-ink">{inr(stats.overall.earnings)}</p><p className="text-[11px] text-ink-soft">Total earnings</p></div>
              <div className="rounded-xl bg-surface-50 p-2.5 text-center"><p className="inline-flex items-center gap-0.5 text-lg font-bold text-ink">{stats.overall.rating ? <>{stats.overall.rating}<Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /></> : "—"}</p><p className="text-[11px] text-ink-soft">{stats.overall.rating_count} ratings</p></div>
            </div>
            <p className="mb-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="pc-earnings-note">You keep 100% of your daily fare — it's paid to you directly by the customer. The ₹315 platform fee is paid by the customer to KohimaTaxi and is <strong>not deducted</strong> from your earnings.</p>
            {stats.overall.rating_count > 0 && (
              <div className="mb-3 rounded-xl border border-surface-200 p-3">
                <p className="mb-1.5 text-xs font-semibold text-ink-soft">Rating breakdown</p>
                <RatingBreakdown breakdown={stats.overall.breakdown} total={stats.overall.rating_count} testId="pc-stats-breakdown" />
              </div>
            )}
            <div className="space-y-1.5" data-testid="pc-stats-months">
              {stats.months.map((m) => (
                <div key={m.month} className="flex items-center justify-between rounded-xl border border-surface-300 px-3 py-2 text-sm" data-testid={`pc-stats-month-${m.month}`}>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-ink"><TrendingUp className="h-4 w-4 text-brand" /> {m.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-ink-soft">{m.trips} trip{m.trips === 1 ? "" : "s"} · <span className="font-semibold text-ink">{inr(m.earnings)}</span>{m.avg_rating ? <> · {m.avg_rating}★</> : ""}</span>
                    <a href={privateCarApi.earningsExportUrl(m.month)} download className="text-ink-muted hover:text-brand" title={`Download ${m.label} statement`} data-testid={`pc-earnings-export-${m.month}`}><Download className="h-4 w-4" /></a>
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {mine.length > 0 && (
          <Section title="Your bookings">
            <div className="space-y-2" data-testid="pc-mine">
              {mine.map((b) => (
                <div key={b.booking_id} className="rounded-2xl border border-surface-300 bg-white p-3" data-testid={`pc-mine-${b.booking_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-ink">{new Date(b.start_date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {b.days} day{b.days > 1 ? "s" : ""}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="mt-1 text-xs text-ink-soft">{b.pickup_address} · {b.customer_name}</p>
                  <p className="mt-1 text-xs text-ink-soft">{inr(b.daily_rate)}/day × {b.days} = <span className="font-semibold text-ink">{inr(b.fare)}</span> <span className="text-ink-muted">(paid to you directly)</span> · KohimaTaxi fee ₹{b.platform_fee ?? 315} <span className="text-ink-muted">(paid upfront by customer)</span></p>
                  {b.status === "confirmed" && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button onClick={() => run(b.booking_id, privateCarApi.complete, "Trip marked complete")} disabled={busyId === b.booking_id} size="sm" className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`pc-complete-${b.booking_id}`}>
                        {busyId === b.booking_id ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Flag className="mr-1.5 h-3.5 w-3.5" />} Complete trip
                      </Button>
                      <Button
                        onClick={() => { if (window.confirm("Cancel this booking? The customer's ₹315 platform fee will be refunded in full and the booking will be closed.")) run(b.booking_id, privateCarApi.driverCancel, "Booking cancelled — customer refunded ₹315"); }}
                        disabled={busyId === b.booking_id} size="sm" variant="outline"
                        className="rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid={`pc-driver-cancel-${b.booking_id}`}>
                        <Ban className="mr-1.5 h-3.5 w-3.5" /> Cancel booking
                      </Button>
                    </div>
                  )}
                  {b.status === "cancelled" && b.driver_cancelled && (
                    <p className="mt-2 text-xs text-red-600" data-testid={`pc-driver-cancelled-${b.booking_id}`}>You cancelled this booking — the customer's ₹315 was refunded.</p>
                  )}
                  {b.status === "completed" && b.payment_status !== "paid" && (
                    <p className="mt-2 text-xs text-ink-muted">Trip complete. The customer's ₹315 platform fee was paid upfront — nothing more to collect.</p>
                  )}
                  {b.status === "settled" && <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" /> Settled {b.payment_method === "cash" ? "in cash" : "online"}</p>}
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title="Reminder settings" description="Choose how early you want pickup reminders. Customers always get 24h and 2h reminders.">
          <div className="flex flex-wrap gap-2" data-testid="pc-reminder-prefs">
            {REMINDER_CHOICES.map((h) => {
              const on = prefs.includes(h);
              return (
                <button key={h} type="button" onClick={() => togglePref(h)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${on ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft"}`} data-testid={`pc-reminder-${h}`}>
                  <Bell className="h-3.5 w-3.5" /> {h === 1 ? "1 hour" : `${h} hours`} before
                </button>
              );
            })}
          </div>
          {prefs.length === 0 && <p className="mt-2 text-xs text-ink-muted">No reminders selected — you won't get pickup alerts.</p>}
        </Section>

        <Section title="Your reviews" description={myRating.rating_count > 0 ? `${myRating.rating}★ from ${myRating.rating_count} trip${myRating.rating_count > 1 ? "s" : ""}` : "No reviews yet"}>
          {reviews.length === 0 ? (
            <EmptyState icon={Star} title="No reviews yet" text="Ratings from your completed private-car trips will show here." />
          ) : (
            <div className="space-y-2" data-testid="pc-reviews">
              {reviews.map((rv) => (
                <div key={rv.review_id} className="rounded-2xl border border-surface-300 bg-white p-3" data-testid={`pc-review-${rv.review_id}`}>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (<Star key={s} className={`h-4 w-4 ${s <= rv.stars ? "fill-amber-400 text-amber-400" : "text-surface-300"}`} />))}
                    <span className="ml-1 text-xs text-ink-soft">{rv.customer_name || "Customer"}</span>
                  </div>
                  {rv.comment && <p className="mt-1 text-sm text-ink">{rv.comment}</p>}
                  {rv.photo_ids?.length > 0 && (
                    <div className="mt-1.5 flex gap-1.5" data-testid={`pc-review-photos-${rv.review_id}`}>
                      {rv.photo_ids.map((pid) => (
                        <img key={pid} src={privateCarApi.photoUrl(pid)} alt="review" className="h-16 w-16 rounded-lg object-cover" loading="lazy" />
                      ))}
                    </div>
                  )}
                  {rv.driver_reply ? (
                    <div className="mt-2 rounded-xl bg-surface-50 p-2 text-xs" data-testid={`pc-review-reply-${rv.review_id}`}>
                      <p className="font-semibold text-ink-soft">Your reply</p>
                      <p className="text-ink">{rv.driver_reply}</p>
                    </div>
                  ) : replyFor === rv.review_id ? (
                    <div className="mt-2 flex gap-2">
                      <Input autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a short reply…" className="h-9" data-testid={`pc-reply-input-${rv.review_id}`} />
                      <Button size="sm" onClick={() => sendReply(rv.review_id)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`pc-reply-send-${rv.review_id}`}><Send className="h-3.5 w-3.5" /></Button>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-3">
                      <button type="button" onClick={() => { setReplyFor(rv.review_id); setReplyText(""); }} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline" data-testid={`pc-reply-btn-${rv.review_id}`}><MessageSquare className="h-3.5 w-3.5" /> Reply</button>
                      {rv.reviewer_id && <button type="button" onClick={() => blockReviewer(rv.review_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline" data-testid={`pc-review-block-${rv.review_id}`}><Ban className="h-3.5 w-3.5" /> Block reviewer</button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </>
  );
}
